import { useState, useEffect, useCallback } from 'react';
import { formatToLocaleDate, parseDateString } from '../../../../helpers/dateUtils';
import actasApiService from '../../../../services/http/actasApiService';
import additioApiService from '../../../../services/http/additioApiService';
import funcionalidadesApiService from '../../../../services/http/funcionalidadesApiService';

export default function useFormularioActa(isActa, importMode = 'notas') {
  const convocatoriaToType = (conv) => (conv === 'extraordinaria' ? 2 : 1);
  const [data, setData] = useState('');
  const [headquarter, setHeadquarter] = useState('CEDESCA');
  const [courseYear, setCourseYear] = useState('2324');
  const [course, setCourse] = useState(1);
  const [isLoadingActivitiesList, setIsLoadingActivitiesList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProceedingLoading, setIsProceedingLoading] = useState(false);
  const [type, setType] = useState(1);
  const [group, setGroup] = useState();
  const [fecha, setFecha] = useState('');
  const [convocatoria, setConvocatoria] = useState('ordinaria');
  const [activities, setActivities] = useState([]);
  const [activitiesList, setActivitiesList] = useState([]);
  const [listadoAlumnos, setListadoAlumnos] = useState([]);
  const [alumnosIds, setAlumnosIds] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [lmsActivo, setLmsActivo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dataActas = await actasApiService.cargarActas({
          degree: '1',
          years: '2425',
          headquarter: 'CEDESCA',
          type: '1',
        });
        setGroup(dataActas.groups[0].titulo);
        setData(dataActas);
      } catch (error) {
        setGroup();
        setData('');
      }
      try {
        const lmsResponse = await funcionalidadesApiService.obtenerLmsActivo();
        setLmsActivo(lmsResponse.lms || null);
      } catch {
        setLmsActivo(null);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoadingActivitiesList(true);
      try {
        const activitiesResponse = await actasApiService.cargarActividades({
          headquarter,
          group,
          type,
        });
        setActivitiesList(activitiesResponse);
      } catch (error) {
        setActivitiesList([]);
      } finally {
        setActivities([]);
        setIsLoadingActivitiesList(false);
      }
    };
    if (headquarter && group && type) {
      fetchActivities();
    } else {
      setActivities([]);
      setActivitiesList([]);
    }
  }, [group, headquarter, type]);

  const allChecked =
    listadoAlumnos.length > 0 && listadoAlumnos.every((a) => alumnosIds.includes(a.ccodcli));

  const handleStudent = useCallback((ccodcli) => {
    setAlumnosIds((prev) =>
      prev.includes(ccodcli) ? prev.filter((id) => id !== ccodcli) : [...prev, ccodcli],
    );
  }, []);

  const handleStudentsVisibles = useCallback((filas, marcar) => {
    const ids = filas.map((f) => f.ccodcli);
    setAlumnosIds((prev) => {
      const resto = prev.filter((id) => !ids.includes(id));
      return marcar ? [...resto, ...ids] : resto;
    });
  }, []);

  const handleHeadquarter = async (e) => {
    setIsLoading(true);
    setHeadquarter(e.target.value);
    try {
      const dataActas = await actasApiService.cargarActas({
        degree: course,
        years: courseYear,
        headquarter: e.target.value,
        type,
      });
      setGroup(dataActas.groups[0].titulo);
      setData(dataActas);
    } catch (error) {
      setGroup();
      setData('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseYear = async (e) => {
    setIsLoading(true);
    setCourseYear(e.target.value);
    try {
      const dataActas = await actasApiService.cargarActas({
        degree: course,
        years: e.target.value,
        headquarter,
        type,
      });
      setGroup(dataActas.groups[0].titulo);
      setData(dataActas);
    } catch (error) {
      setGroup();
      setData('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourse = async (e) => {
    setIsLoading(true);
    setCourse(e.target.value);
    try {
      const dataActas = await actasApiService.cargarActas({
        degree: e.target.value,
        years: courseYear,
        headquarter,
        type,
      });
      setGroup(dataActas.groups[0].titulo);
      setData(dataActas);
    } catch (error) {
      setGroup();
      setData('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleType = async (e) => {
    setIsLoading(true);
    setType(e.target.value);
    try {
      const dataActas = await actasApiService.cargarActas({
        degree: course,
        years: courseYear,
        headquarter,
        type: e.target.value,
      });
      setGroup(dataActas.groups[0].titulo);
      setData(dataActas);
    } catch (error) {
      setGroup();
      setData('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGroup = (e) => {
    setGroup(e.target.value);
  };

  const handleConvocatoria = (e) => {
    setConvocatoria(e.target.value);
  };

  const resetSelection = () => {
    setAlumnosIds([]);
    setListadoAlumnos([]);
  };

  const handleGenerate = async () => {
    setIsProceedingLoading(true);
    try {
      const dataBlob = await actasApiService.buscarDatosActas({
        course,
        courseYear,
        type,
        headquarter,
        group,
        fecha,
        usersId: alumnosIds,
        activities: activities.map((act) => act.value),
      });
      const blobFinal = dataBlob.slice(0, dataBlob.size, 'application/pdf');
      const url = URL.createObjectURL(blobFinal);
      window.open(url, '_blank');
      setIsLoading(false);
      setIsProceedingLoading(false);
    } catch (error) {
      const status = error.response?.status;
      const fallbackByStatus = {
        400: 'No se ha podido generar el acta. Revisa que las actividades y los datos seleccionados sean correctos.',
        401: 'Demasiados módulos seleccionados. Reduce la selección de módulos para poder generar el acta.',
      };
      let message = fallbackByStatus[status] || 'No se ha podido generar el acta.';
      const payload = error.response?.data;
      try {
        let parsed = payload;
        if (payload && typeof payload.text === 'function') {
          const text = await payload.text();
          parsed = text ? JSON.parse(text) : null;
        }
        const backendMessage = parsed?.error || parsed?.displayMessage || parsed?.message;
        if (backendMessage) message = backendMessage;
      } catch (parseError) {
        // payload no es JSON legible; conservamos el fallback por status
      }
      setErrorModal({ show: true, message });
      setIsLoading(false);
      setIsProceedingLoading(false);
    }
  };

  const handleProceeding = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    setAlumnosIds([]);
    try {
      const effectiveType = importMode === 'ras' ? convocatoriaToType(convocatoria) : type;
      const dataAlumnosActas = await actasApiService.buscarAlumnosActas({
        type: effectiveType,
        group,
        fecha,
        courseYear,
        headquarter,
        isActa,
      });
      setListadoAlumnos(dataAlumnosActas);
    } catch (error) {
      setListadoAlumnos([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheck = () => {
    setAlumnosIds(allChecked ? [] : listadoAlumnos.map((a) => a.ccodcli));
  };

  const handleFecha = (value) => {
    if (!value) {
      setFecha('');
      return;
    }
    setFecha(formatToLocaleDate(value));
  };

  const handleImport = async () => {
    setIsImporting(true);
    if (lmsActivo === 'moodle') {
      const dataAdditio = { type, group, fecha, courseYear, headquarter };
      await additioApiService.importarNotasAdditio({ dataAdditio, alumnosIds });
    } else {
      const dataD2L = { type, group, fecha, courseYear, headquarter };
      await additioApiService.importarNotasD2L({ dataD2L, alumnosIds });
    }
    setIsImporting(false);
    setIsDone(true);
  };

  const [isImportingRAs, setIsImportingRAs] = useState(false);
  const [isDoneRAs, setIsDoneRAs] = useState(false);

  const handleImportRAs = async () => {
    setIsImportingRAs(true);
    try {
      const tipoConvocatoria = convocatoriaToType(convocatoria);
      const results = await Promise.allSettled(
        alumnosIds.map((userId) =>
          additioApiService.sacarRAS({
            userId,
            headquarter,
            group,
            courseYear,
            type: tipoConvocatoria,
          }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        setErrorModal({
          show: true,
          message: `Se importaron RAs con ${failed.length} error(es).`,
        });
      }
      setIsDoneRAs(true);
    } catch {
      setErrorModal({ show: true, message: 'Error al importar RAs.' });
    } finally {
      setIsImportingRAs(false);
    }
  };

  return {
    data,
    headquarter,
    courseYear,
    course,
    type,
    group,
    fecha,
    activities,
    setActivities,
    activitiesList,
    isLoadingActivitiesList,
    isLoading,
    isProceedingLoading,
    listadoAlumnos,
    alumnosIds,
    allChecked,
    isImporting,
    isDone,
    errorModal,
    setErrorModal,
    isActa,
    handleHeadquarter,
    handleCourseYear,
    handleCourse,
    handleType,
    handleGroup,
    handleConvocatoria,
    convocatoria,
    handleFecha,
    resetSelection,
    handleProceeding,
    handleCheck,
    handleStudent,
    handleStudentsVisibles,
    handleGenerate,
    handleImport,
    handleImportRAs,
    isImportingRAs,
    isDoneRAs,
    parseDateString,
    lmsActivo,
  };
}
